import { Icon } from '@gftg/ui';
import { Link, useParams } from 'react-router';
import { Avatar } from '../components/Avatar';
import { CardRow } from '../components/CardRow';
import { ErrorBlock, LoadingBlock } from '../components/states';
import { formatTime } from '../lib/datetime';
import { sessionPath } from '../lib/paths';
import { useEvent, useSpeaker } from '../lib/queries';

export function SpeakerPage() {
  const { id = '' } = useParams();
  const event = useEvent();
  const speaker = useSpeaker(id);

  if (event.isPending || speaker.isPending) {
    return <LoadingBlock label="Loading the presenter…" />;
  }

  if (event.isError || !event.data) {
    return (
      <ErrorBlock
        message="The conference could not be loaded."
        onRetry={() => void event.refetch()}
      />
    );
  }

  if (speaker.isError || !speaker.data) {
    return <ErrorBlock message="This presenter is not part of the published program." />;
  }

  const { timezone, days } = event.data;
  const person = speaker.data;

  return (
    <>
      <header className="px-gutter border-b border-border-2 bg-surface pt-1 pb-5">
        <Link
          to="/speakers"
          className="text-subtitle -ml-1 inline-flex min-h-touch items-center gap-1 font-semibold text-ink-3"
        >
          <Icon name="chevron-left" size={18} />
          Speakers
        </Link>
        {/* PRG-09 puts the photograph on the presenter's own screen. Above the name rather than
            beside it: this is the page somebody opens to check they are about to walk up to the
            right person. */}
        <Avatar name={person.name} photoAssetId={person.headshotAssetId} size="profile" />
        <h1 className="font-display text-screen-title mt-4 font-semibold text-ink">
          {person.name}
        </h1>
        {(person.titleRole ?? person.organization) && (
          <p className="text-subtitle mt-1 text-ink-3">
            {[person.titleRole, person.organization].filter(Boolean).join(' · ')}
          </p>
        )}
        {person.linkedinUrl && (
          <a
            href={person.linkedinUrl}
            target="_blank"
            rel="noreferrer noopener"
            className="text-chip mt-3 inline-flex min-h-touch items-center gap-1.5 rounded-full border border-border bg-surface px-3.5 py-2 font-semibold text-ink-2"
          >
            LinkedIn
            <Icon name="external" size={12} className="text-hairline" />
          </a>
        )}
      </header>

      <main className="px-gutter space-y-5 py-6">
        {person.bio ? (
          <p className="text-body whitespace-pre-line text-ink-2">{person.bio}</p>
        ) : (
          <p className="text-body text-ink-3">No biography has been published yet.</p>
        )}

        {person.sessions.length > 0 && (
          <section>
            <h2 className="text-eyebrow tracking-eyebrow mb-2 font-bold text-ink-3 uppercase">
              Appearing in
            </h2>
            <ul className="space-y-card">
              {person.sessions.map((session) => {
                const day = days.find((candidate) => candidate.key === session.dayKey);
                return (
                  <li key={session.id}>
                    <CardRow
                      to={sessionPath(session)}
                      title={session.title}
                      subtitle={[
                        day?.label,
                        formatTime(session.startsAt, timezone),
                        session.roomName,
                      ]
                        .filter(Boolean)
                        .join(' · ')}
                    />
                  </li>
                );
              })}
            </ul>
          </section>
        )}
      </main>
    </>
  );
}
